import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import BaseActionButton from '../../../../components/designe/ActionButtons/BaseActionButton';
import DeleteActionButton from '../../../../components/designe/ActionButtons/DeleteActionButton';
import PlayActionButton from '../../../../components/designe/ActionButtons/PlayActionButton';
import AppFormOptionTextField from '../../../../components/designe/app-form-option-text-field/AppFormOptionTextField';
import AppMenu, {
  PositionOption,
  getPosition,
} from '../../../../components/designe/app-menu/AppMenu';
import SetTypeIcon from '../../../../components/reusable/set-type-icon/SetTypeIcon';
import TrainingSetItemStatusIcon from '../../../../components/reusable/training-set-item-status-status/TrainingSetItemStatusIcon';
import { useTrainingSetItemStatusListMode } from '../../../../hooks/use-training-set-item-status-list-mode';
import {
  SetType,
  TrainingSetItemStatus,
  TrainingSetItemStatusListMode,
  sideColumnWidth,
  typeColumnWidth,
} from '../../../../shared/constants';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../types/interfaces/training-set-item-option';
import { getColumnOptions } from '../../../../utils/ui/column-size-utils';
import { useStyles } from './CurrentTrainingSetItem.styles';
import { useCurrentTrainingSetItemComponent } from './useCurrentTrainingSetItemComponent';

interface ITrainingSetItemProps {
  trainingSetItem: ITrainingSetItem;
  trainingSet: ITrainingSet;
  trainingId: string;
  columnWidth: number;
  onActivateTimer: (value: number) => void;
  onItemDelete: (item: ITrainingSetItem) => void;
}

const actionsWidth = 32;
export default function CurrentTrainingSetItem(props: ITrainingSetItemProps) {
  const {
    trainingSetItem,
    trainingId,
    trainingSet,
    onActivateTimer,
    onItemDelete,
    columnWidth,
  } = props;

  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { exerciseOptions, formik, onChangeTrainingSetItemOrder } =
    useCurrentTrainingSetItemComponent(
      trainingSetItem,
      trainingSet.id,
      trainingId,
      onActivateTimer,
    );
  const { t } = useTranslation();
  const statusListMode = useTrainingSetItemStatusListMode();
  const isFirstSetFirstRow =
    trainingSet.setNumber === 0 &&
    trainingSet.orderNumber === 0 &&
    trainingSetItem.orderNumber === 0;
  // const dndAccept = `currentTrainingSet_${trainingSet.setNumber}_${trainingSet.orderNumber}`;
  const [typeAnchorEl, setTypeAnchorEl] = useState<HTMLElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const ref = useRef(null);
  // const [{ isOver, canDrop }, drop] = useDrop(
  //   {
  //     accept: dndAccept,
  //     drop: (trainingSetItem) => {
  //       onChangeTrainingSetItemOrder(trainingSetItem);
  //     },
  //     canDrop: ({ orderNumber }: ITrainingSetItem) =>
  //       orderNumber !== trainingSetItem.orderNumber,
  //     collect: (monitor) => ({
  //       isOver: monitor.isOver(),
  //       canDrop: monitor.canDrop(),
  //     }),
  //   },
  //   [trainingSetItem],
  // );
  //
  // const [{ isDragging }, drag] = useDrag(
  //   () => ({
  //     type: dndAccept,
  //     item: () => trainingSetItem,
  //     collect: (monitor) => ({
  //       isDragging: !!monitor.isDragging(),
  //     }),
  //   }),
  //   [trainingSetItem],
  // );

  function renderOptionField(
    setOption: ITrainingSetItemOption,
    index: number,
    valueFieldName: 'value' | 'value2' = 'value',
  ) {
    if (!exerciseOptions) return null;

    const option = exerciseOptions?.find((e) => e.id === setOption.optionId);
    const optionTimerId = `currentTimer-${trainingSetItem.id}-option-${option.nameKey}`;

    const fieldName = `trainingSetItemOptions[${index}].${valueFieldName}`;

    const placeholderValue =
      valueFieldName === 'value2'
        ? formik.values.trainingSetItemOptions?.[index]?.programValue2
        : formik.values.trainingSetItemOptions?.[index]?.programValue;

    return (
      <Box {...getColumnOptions(columnWidth)} key={option?.nameKey}>
        <AppFormOptionTextField
          formik={formik}
          placeholder={placeholderValue || option?.name}
          endAdornment={option?.unit}
          name={fieldName}
          type={option?.type}
          // playing={activateTimerId === optionTimerId}
          // onPlayClick={() =>
          //   onActivateTimer({
          //     type: option.type as ExerciseOptionType,
          //     timerId: optionTimerId,
          //     time:
          //       option?.type === ExerciseOptionType.time
          //         ? formik.values.trainingSetItemOptions?.[index]?.programValue
          //         : 0,
          //     autostart: false,
          //     startValue: 0,
          //     onFinish: (value) => formik.setFieldValue(fieldName, value),
          //     label: option?.name,
          //   })
          // }
        />
      </Box>
    );
  }

  function getStatusClass(): string {
    if (formik.values.status === TrainingSetItemStatus.started)
      return TrainingSetItemStatus.started;

    const statusForStyle = formik.values.status;

    return `${statusForStyle}${trainingSetItem.orderNumber % 2 === 1 ? 'OddLine' : 'EvenLine'}`;
  }

  function renderSetType() {
    return (
      <Box {...getColumnOptions(typeColumnWidth)}>
        <AppMenu
          anchorEl={typeAnchorEl}
          onClose={() => setTypeAnchorEl(null)}
          className={classes.menuContainer}
          anchorOrigin={getPosition(PositionOption.bottom, PositionOption.left)}
          transformOrigin={getPosition(PositionOption.top, PositionOption.left)}
          targetElement={
            <BaseActionButton
              onClick={(e) => {
                setTypeAnchorEl(e.currentTarget as any);
              }}>
              <SetTypeIcon type={formik.values.type} />
            </BaseActionButton>
          }>
          {Object.values(SetType).map((type) => (
            <MenuItem
              key={type}
              onClick={() => {
                formik.setFieldValue('type', type);
                setTypeAnchorEl(null);
              }}
              className={classes.menuItemContainer}>
              <SetTypeIcon type={type} />
              {t(`common.setType_${type}`)}
            </MenuItem>
          ))}
        </AppMenu>
      </Box>
    );
  }

  function renderSetStatus() {
    if (statusListMode === TrainingSetItemStatusListMode.primary) {
      const displayStatus = formik.values.status;

      return (
        <BaseActionButton
          data-onboarding={
            isFirstSetFirstRow
              ? 'currentTraining.firstSetFirstRowStatus'
              : undefined
          }
          onClick={() => {
            if (formik.values.status === TrainingSetItemStatus.finished) {
              formik.setFieldValue('status', TrainingSetItemStatus.notStarted);
            } else {
              formik.setFieldValue('status', TrainingSetItemStatus.finished);
            }
          }}>
          <TrainingSetItemStatusIcon status={displayStatus} />
        </BaseActionButton>
      );
    }

    const statuses = [
      TrainingSetItemStatus.notStarted,
      TrainingSetItemStatus.finished,
      TrainingSetItemStatus.doLate,
      TrainingSetItemStatus.skipped,
    ].filter((status) => status !== formik.values.status);

    return (
      <AppMenu
        anchorEl={statusAnchorEl}
        onClose={() => setStatusAnchorEl(null)}
        className={classes.menuContainer}
        anchorOrigin={getPosition(PositionOption.bottom, PositionOption.right)}
        transformOrigin={getPosition(PositionOption.top, PositionOption.right)}
        targetElement={
          <BaseActionButton
            data-onboarding={
              isFirstSetFirstRow
                ? 'currentTraining.firstSetFirstRowStatus'
                : undefined
            }
            onClick={(e) => {
              setStatusAnchorEl(e.currentTarget as any);
            }}>
            <TrainingSetItemStatusIcon status={formik.values.status} />
          </BaseActionButton>
        }>
        {statuses.map((status) => (
          <MenuItem
            key={status}
            onClick={() => {
              formik.setFieldValue('status', status);
              setStatusAnchorEl(null);
            }}
            className={classes.menuItemContainer}>
            <TrainingSetItemStatusIcon status={status} />
            {t(`trainings.setItemStatus_${status}`)}
          </MenuItem>
        ))}
      </AppMenu>
    );
  }

  // const isDragTarget = isOver && canDrop;
  // drag(drop(ref));

  return (
    <Box
      data-onboarding={
        isFirstSetFirstRow ? 'currentTraining.firstSetFirstRow' : undefined
      }
      ref={ref}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      className={clsx(
        classes.trainingSetWrapper,
        classes[getStatusClass()],
        {
          [classes.trainingSetWrapperWithSplitBySideExercise]:
            trainingSet?.exercise?.splitBySide,
        },
        // isDragging && classes.itemDragging,
        // isDragTarget && classes.itemDragTarget,
      )}>
      {renderSetType()}
      {!!trainingSetItem?.trainingSetItemOptions?.length &&
        formik.values.trainingSetItemOptions?.length && (
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            width="100%">
            <Box display="flex" alignItems="center">
              {trainingSet?.exercise?.splitBySide && (
                <Box {...getColumnOptions(sideColumnWidth)}>
                  <Typography
                    variant={'label-text-sm'}
                    className={clsx(classes.sideLabel, classes.rightSideLabel)}
                    component="div">
                    {t('trainings.rightShort')}
                  </Typography>
                </Box>
              )}
              {formik.values.trainingSetItemOptions.map((setOption, index) =>
                renderOptionField(setOption, index),
              )}
            </Box>
            {trainingSet?.exercise?.splitBySide && (
              <Box display="flex" alignItems="center">
                <Box {...getColumnOptions(sideColumnWidth)}>
                  <Typography
                    variant={'label-text-sm'}
                    className={clsx(classes.sideLabel, classes.leftSideLabel)}>
                    {t('trainings.leftShort')}
                  </Typography>
                </Box>
                {formik.values.trainingSetItemOptions.map((setOption, index) =>
                  renderOptionField(setOption, index, 'value2'),
                )}
              </Box>
            )}
          </Box>
        )}
      <Box
        {...getColumnOptions(actionsWidth)}
        display="flex"
        alignItems="center"
        gap={0.125}>
        {renderSetStatus()}
        {/* <PlayActionButton
          playing={activateTimerId === restTimerId}
          disabled={
            activateTimerId === restTimerId ||
            trainingSetItem.status !== TrainingSetItemStatus.finished
          }
          onClick={() => {
            if (activateTimerId !== restTimerId) {
              onActivateTimer({
                type: 'time',
                timerId: restTimerId,
                time: trainingSetItem.rest,
                autostart: true,
                onFinish: () => onActivateTimer(null),
                label: t('common.rest'),
              });
            }
          }}
        /> */}
        {/* <DeleteActionButton onClick={() => onItemDelete(trainingSetItem)} /> */}
      </Box>
    </Box>
  );
}
